/**
 * The article and store labels the Health files carry beside their rows — the client's request of
 * 2026-11-03 (Brand, Gender, Major Category, Category, Risk, the store's code and name), owner answers
 * the same day: each report's **by-article file** and its **by-article drill files**, as **export
 * columns only** (no page gains a column).
 *
 * **Where the labels come from.** `POST /api/v1/health/attributes` (bata-platform ADR 9053 amended
 * 2026-11-04): the attribute row in force on the file's own date, and the market's door master in
 * force — the replenishment report's rule and reasons, so the words match the run's files. The file's
 * figures are read exactly as before; this read only names the keys the file already holds, in chunks
 * of the platform's cap.
 *
 * **Never a guess.** A label the platform does not publish, a key neither master lists, a read that
 * fails, or a platform that does not serve the read yet: the cell is `not-reported`, the file is still
 * written, and its figures are untouched.
 */

import { callOperation } from '@/api';
import { postAttributes, type ArticleAttributesModel, type AttributesResponseModel, type StoreNameModel } from '@/api/generated';
import type { ExportCell, ExportColumn } from '@/export';

/** The platform's cap on distinct keys of one kind per request (ADR 9053 amended 2026-11-04). */
export const ATTRIBUTES_CHUNK = 2000;

/** What a file knows of its keys' labels: by article and by store; an unread key is absent. */
export interface HealthAttributes {
  readonly articles: ReadonlyMap<string, ArticleAttributesModel>;
  readonly stores: ReadonlyMap<string, StoreNameModel>;
}

export const NO_ATTRIBUTES: HealthAttributes = { articles: new Map(), stores: new Map() };

function chunks(keys: readonly string[]): readonly (readonly string[])[] {
  const distinct = [...new Set(keys.map((key) => key.trim()).filter((key) => key !== ''))];
  const out: string[][] = [];
  for (let start = 0; start < distinct.length; start += ATTRIBUTES_CHUNK) out.push(distinct.slice(start, start + ATTRIBUTES_CHUNK));
  return out;
}

/**
 * Every asked article's and store's labels, in as few requests as the cap allows. Never rejects:
 * a request that fails leaves its keys unread, so their cells are `not-reported`.
 */
export async function readHealthAttributes(
  market: string | null,
  asOf: string | null,
  articles: readonly string[],
  stores: readonly string[] = [],
): Promise<HealthAttributes> {
  if (market === null || asOf === null) return NO_ATTRIBUTES;
  const articleChunks = chunks(articles);
  const storeChunks = chunks(stores);
  const requests = Math.max(articleChunks.length, storeChunks.length);
  const found = { articles: new Map<string, ArticleAttributesModel>(), stores: new Map<string, StoreNameModel>() };
  for (let index = 0; index < requests; index += 1) {
    let answer: AttributesResponseModel;
    try {
      answer = await callOperation(postAttributes, {
        body: { market, asOf, articles: articleChunks[index] ?? [], stores: storeChunks[index] ?? [] },
      });
    } catch {
      continue;
    }
    for (const item of answer.articles) found.articles.set(item.articleKey, item);
    for (const item of answer.stores) found.stores.set(item.storeKey, item);
  }
  return found;
}

const missing: ExportCell = { kind: 'missing', state: 'not-reported' };
const text = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === '' ? missing : { kind: 'text', value });

/** The five article columns, in the client's order. Literal keys, so the catalogue check sees each. */
export const ARTICLE_ATTRIBUTE_COLUMNS: readonly ExportColumn[] = [
  { key: 'brand', headerKey: 'planning.healthAttributes.column.brand', descriptionKey: 'planning.healthAttributes.columnGuide.brand', kind: 'text' },
  { key: 'gender', headerKey: 'planning.healthAttributes.column.gender', descriptionKey: 'planning.healthAttributes.columnGuide.gender', kind: 'text' },
  { key: 'major_category', headerKey: 'planning.healthAttributes.column.majorCategory', descriptionKey: 'planning.healthAttributes.columnGuide.majorCategory', kind: 'text' },
  { key: 'category', headerKey: 'planning.healthAttributes.column.category', descriptionKey: 'planning.healthAttributes.columnGuide.category', kind: 'text' },
  { key: 'risk', headerKey: 'planning.healthAttributes.column.risk', descriptionKey: 'planning.healthAttributes.columnGuide.risk', kind: 'text' },
];

/** The store's name, beside the store code the file already carries. */
export const STORE_NAME_COLUMN: ExportColumn = {
  key: 'store_name', headerKey: 'planning.healthAttributes.column.storeName', descriptionKey: 'planning.healthAttributes.columnGuide.storeName', kind: 'text',
};

/** One article's five cells, in `ARTICLE_ATTRIBUTE_COLUMNS` order. Pure. */
export function articleAttributeCells(found: HealthAttributes, articleKey: string): readonly ExportCell[] {
  const item = found.articles.get(articleKey);
  return [text(item?.brand), text(item?.gender), text(item?.majorCategory), text(item?.category), text(item?.risk)];
}

/** One store's name cell. Pure. */
export function storeNameCell(found: HealthAttributes, storeKey: string): ExportCell {
  return text(found.stores.get(storeKey)?.storeName);
}
